import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import getCallMeBy from '@/components/user/get-call-me-by';
import useUsers from '@/hooks/use-users';
import { cn, pluralize } from '@/lib/utils';
import type { FinanceManager } from '@/models/types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useFilteredDealsContext } from '../filtered-deals/filtered-deals-provider';
import type { FinanceManagerFilters } from '../types';

const FinanceManagerFiltersComboBoxPopover = ({
  children,
  comboBoxOnChange,
  comboBoxOpen,
  onOpenChange,
  filters: financeManagerFilters,
  stayOpenAfterSelection = true,
  alignOffset,
  side = 'bottom',
  align,
  popoverContentClassName,
}: {
  children: ReactNode;
  comboBoxOnChange: (value: string) => void;
  comboBoxOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: FinanceManagerFilters;
  stayOpenAfterSelection: boolean;
  alignOffset?: number;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  popoverContentClassName?: string;
}) => {
  const { financeManagers } = useUsers();
  const { getCount } = useFilteredDealsContext();

  const comboBoxOptions = useMemo(() => {
    return financeManagers.map((manager) => {
      const count = getCount('finance_manager', manager.id);

      return {
        label: (
          <div className="flex flex-row items-center gap-2 text-nowrap">
            <PaddockAvatar
              src={manager.avatar}
              alt={`${manager.first_name} ${manager.last_name}`}
              fallback={`${manager.first_name.charAt(0)} ${manager.last_name.charAt(0)}`}
              className="size-4"
            />
            {getCallMeBy(manager)}
            <span className="text-xs text-gray-400">
              {count} {pluralize(count, 'deal', 'deals')}
            </span>
          </div>
        ),
        value: manager.id,
        icon: (
          <div className="size-4">
            <Checkbox
              checked={
                financeManagerFilters[manager.id as FinanceManager['id']]
              }
            />
          </div>
        ),
      };
    });
  }, [financeManagerFilters, getCount]);

  return (
    <ComboboxPopover
      placeholder={'Finance Manager...'}
      options={comboBoxOptions}
      onChange={comboBoxOnChange}
      align={align}
      side={side}
      alignOffset={alignOffset}
      // TODO: Add end adornment and empty message
      endAdornment={undefined}
      emptyMessage={undefined}
      open={comboBoxOpen}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={stayOpenAfterSelection}
      popoverContentClassName={cn(
        'w-fit min-w-[200px]',
        popoverContentClassName,
      )}
    >
      {children}
    </ComboboxPopover>
  );
};

export default FinanceManagerFiltersComboBoxPopover;
